/**
 * EntityField - Generic field cell for entity change cards.
 *
 * Renders compact inline for unchanged fields, card-style with amber background for changed.
 */

import { Check, Copy } from 'lucide-react';
import { useCallback, useState } from 'react';

import { formatEnumValue, formatFieldValue } from '../utils/formatting';
import type { ChangedFieldsInfo } from '../utils/useChangedFields';
import { ChangedFieldIndicator } from './ChangeIndicators';

const COPYABLE_KEYWORDS =
  /(email|phone|fax|website|url|address|policy|claim|member|group|number|identifier|contact|city|state|zip|postal|attn)/i;

function getCopyText(value: unknown): string | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '\u2014') return null;
    return trimmed;
  }

  if (typeof value === 'number') return String(value);

  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    const joined = value.join(', ').trim();
    return joined || null;
  }

  return null;
}

function looksCopyable(fieldKey: string, label: string, text: string): boolean {
  if (COPYABLE_KEYWORDS.test(fieldKey) || COPYABLE_KEYWORDS.test(label)) {
    return true;
  }

  // Fallback for common contact-like values even if field names are generic.
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
  const isUrl = /^https?:\/\//i.test(text);
  const isPhoneLike = /^[+()\d\s.-]{7,}$/.test(text) && /\d{7,}/.test(text);

  return isEmail || isUrl || isPhoneLike;
}

export const EntityField: React.FC<{
  label: string;
  value: unknown;
  fieldKey: string;
  changes: ChangedFieldsInfo;
  isDate?: boolean;
  isEnum?: boolean;
}> = ({ label, value, fieldKey, changes, isDate, isEnum }) => {
  const [copied, setCopied] = useState(false);
  const isChanged = changes.isFieldChanged(fieldKey);
  const displayValue = isEnum
    ? (formatEnumValue(value as string) ?? '\u2014')
    : formatFieldValue(value, isDate);
  const copyText = getCopyText(value);
  const canCopy =
    !isDate &&
    Boolean(copyText) &&
    looksCopyable(fieldKey, label, copyText ?? '');

  const handleCopy = useCallback(async () => {
    if (!copyText) return;
    await navigator.clipboard.writeText(copyText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }, [copyText]);

  return (
    <div
      className={`flex items-center gap-1.5 px-1 py-0.5 rounded ${
        isChanged ? 'bg-amber-50/60' : ''
      }`}
    >
      <span className="text-xs text-gray-400 shrink-0">{label}:</span>
      <span
        className={`text-sm whitespace-normal break-words ${isChanged ? 'text-gray-900' : 'text-gray-700'}`}
      >
        {displayValue}
      </span>
      {canCopy && (
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
          title={copied ? 'Copied' : `Copy ${label}`}
          aria-label={copied ? `${label} copied` : `Copy ${label}`}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      )}
      {isChanged && (
        <ChangedFieldIndicator
          oldValue={changes.getOldValue(fieldKey)}
          label={label}
        />
      )}
    </div>
  );
};
